import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <Link 
          to="/"
          className="inline-flex items-center gap-1 text-[#1E3A5F] hover:text-[#0A1929] mb-6 transition-colors"
        >
          <span className="material-symbols-outlined">arrow_back</span>
          Tilbake
        </Link>

        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-10">
          <h1 className="font-heading font-bold text-3xl text-[#0A1929] mb-6">
            Personvernerklæring
          </h1>

          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 mb-6">
              Sist oppdatert: 13. januar 2026
            </p>

            <section className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                1. Introduksjon
              </h2>
              <p className="text-slate-600">
                Minitask er forpliktet til å beskytte ditt personvern. Denne personvernerklæringen 
                forklarer hvordan vi samler inn, bruker, lagrer og beskytter dine personopplysninger 
                i samsvar med GDPR (General Data Protection Regulation) og norsk personvernlovgivning.
              </p>
              <p className="text-slate-600 mt-3">
                Ved å bruke Minitask godtar du behandlingen av dine personopplysninger som beskrevet 
                i denne erklæringen.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                2. Behandlingsansvarlig
              </h2>
              <p className="text-slate-600">
                Behandlingsansvarlig for personopplysningene som samles inn via Minitask er:
              </p>
              <div className="mt-3 p-4 bg-slate-50 rounded-lg text-slate-700">
                <strong>Minitask AS</strong><br />
                E-post: personvern@minitask.no
              </div>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                3. Hvilke opplysninger vi samler inn
              </h2>
              <p className="text-slate-600 mb-3">
                Vi samler inn følgende personopplysninger:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Kontaktinformasjon:</strong> Navn, e-postadresse og telefonnummer</li>
                <li><strong>Profilinformasjon:</strong> Profilbilde, lokasjon (fylke/by), fødselsdato og biografi</li>
                <li><strong>Brukerdata:</strong> Annonser du oppretter, meldinger, tilbakemeldinger og transaksjonshistorikk</li>
                <li><strong>Teknisk data:</strong> IP-adresse, nettlesertype, enhetsinfo og operativsystem</li>
                <li><strong>Cookie-data:</strong> Informasjon om din bruk av nettsiden via cookies og lignende teknologier</li>
                <li><strong>Analytisk data:</strong> Anonymisert bruksstatistikk for å forbedre tjenesten</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                4. Hvordan vi bruker opplysningene
              </h2>
              <p className="text-slate-600 mb-3">
                Dine personopplysninger brukes til å:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Opprette og administrere din brukerkonto</li>
                <li>Koble oppdragsgivere med arbeidstakere i samme område</li>
                <li>Behandle kommunikasjon mellom brukere</li>
                <li>Sende deg viktige meldinger om tjenesten</li>
                <li>Forbedre våre tjenester og brukeropplevelse</li>
                <li>Forhindre svindel og sikre plattformen</li>
                <li>Analysere bruksmønstre for å forbedre funksjonalitet</li>
                <li>Oppfylle juridiske forpliktelser</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                5. Rettslig grunnlag
              </h2>
              <p className="text-slate-600">
                Vi behandler dine personopplysninger basert på:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-3">
                <li><strong>Samtykke (GDPR Art. 6(1)(a)):</strong> Du gir oss tillatelse ved registrering og for cookies</li>
                <li><strong>Kontrakt (GDPR Art. 6(1)(b)):</strong> Nødvendig for å levere tjenesten</li>
                <li><strong>Berettiget interesse (GDPR Art. 6(1)(f)):</strong> For å forbedre og sikre plattformen</li>
                <li><strong>Juridisk forpliktelse (GDPR Art. 6(1)(c)):</strong> For å oppfylle lovkrav</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                6. Cookies og sporingsteknologi
              </h2>
              <p className="text-slate-600 mb-4">
                Vi bruker cookies og lignende teknologier for å forbedre din opplevelse på Minitask. 
                Her er en oversikt over cookiene vi bruker:
              </p>
              
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <span className="material-symbols-outlined text-lg">check_circle</span>
                    Nødvendige cookies
                  </h4>
                  <p className="text-green-700 text-sm">
                    Disse cookiene er nødvendige for at nettsiden skal fungere og kan ikke slås av. 
                    De inkluderer autentisering, øktinformasjon og sikkerhetsinnstillinger.
                  </p>
                  <ul className="list-disc pl-6 text-green-700 text-sm mt-2 space-y-1">
                    <li><strong>auth_token:</strong> Holder deg innlogget</li>
                    <li><strong>session_id:</strong> Opprettholder økten din</li>
                    <li><strong>cookie_consent:</strong> Husker dine cookie-preferanser</li>
                  </ul>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <span className="material-symbols-outlined text-lg">analytics</span>
                    Analytiske cookies
                  </h4>
                  <p className="text-blue-700 text-sm">
                    Disse hjelper oss å forstå hvordan besøkende bruker nettsiden, slik at vi kan 
                    forbedre brukeropplevelsen. Dataen er anonymisert.
                  </p>
                  <ul className="list-disc pl-6 text-blue-700 text-sm mt-2 space-y-1">
                    <li><strong>_ga, _gid:</strong> Google Analytics - måler trafikk og brukeradferd</li>
                    <li><strong>_gat:</strong> Google Analytics - begrenser antall forespørsler</li>
                  </ul>
                </div>

                <div className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                    <span className="material-symbols-outlined text-lg">tune</span>
                    Funksjonelle cookies
                  </h4>
                  <p className="text-purple-700 text-sm">
                    Disse husker dine preferanser og valg for å gi deg en bedre opplevelse.
                  </p>
                  <ul className="list-disc pl-6 text-purple-700 text-sm mt-2 space-y-1">
                    <li><strong>location_pref:</strong> Husker ditt valgte fylke/by</li>
                    <li><strong>theme_pref:</strong> Husker dine visningsinnstillinger</li>
                  </ul>
                </div>
              </div>

              <p className="text-slate-600 mt-4">
                Du kan når som helst endre dine cookie-preferanser via innstillinger i nettleseren 
                din eller ved å klikke på cookie-innstillinger nederst på siden.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                7. Tredjepartstjenester
              </h2>
              <p className="text-slate-600 mb-3">
                Vi bruker følgende tredjepartstjenester som kan behandle dine personopplysninger:
              </p>
              
              <div className="space-y-3">
                <div className="p-4 border border-slate-200 rounded-lg">
                  <h4 className="font-semibold text-[#0A1929] mb-1">Google Analytics</h4>
                  <p className="text-slate-600 text-sm">
                    Vi bruker Google Analytics for å analysere hvordan nettsiden brukes. 
                    IP-adresser anonymiseres. Data overføres til Google i USA under 
                    EU-US Data Privacy Framework.
                  </p>
                  <a 
                    href="https://policies.google.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#1E3A5F] text-sm hover:underline"
                  >
                    Les Googles personvernerklæring
                  </a>
                </div>

                <div className="p-4 border border-slate-200 rounded-lg">
                  <h4 className="font-semibold text-[#0A1929] mb-1">Google Search Console</h4>
                  <p className="text-slate-600 text-sm">
                    Vi bruker Google Search Console for å overvåke og forbedre nettsidens 
                    synlighet i søkeresultater. Dette verktøyet samler ikke inn personopplysninger 
                    direkte fra brukere.
                  </p>
                </div>

                <div className="p-4 border border-slate-200 rounded-lg">
                  <h4 className="font-semibold text-[#0A1929] mb-1">Hosting og infrastruktur</h4>
                  <p className="text-slate-600 text-sm">
                    Nettsiden hostes på servere i EU/EØS-området for å sikre at dine data 
                    behandles i samsvar med GDPR.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                8. Deling av opplysninger
              </h2>
              <p className="text-slate-600">
                Vi deler kun dine opplysninger med:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-3">
                <li><strong>Andre brukere:</strong> Begrenset kontaktinfo vises til relevante parter ved oppdrag</li>
                <li><strong>Analyseleverandører:</strong> Anonymisert bruksdata til Google Analytics</li>
                <li><strong>Myndigheter:</strong> Hvis pålagt ved lov eller rettslig kjennelse</li>
              </ul>
              <p className="text-slate-600 mt-3">
                Vi selger aldri dine personopplysninger til tredjeparter.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                9. Dine rettigheter
              </h2>
              <p className="text-slate-600 mb-3">
                I henhold til GDPR har du følgende rettigheter:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>Innsyn:</strong> Du kan be om kopi av dine data</li>
                <li><strong>Retting:</strong> Du kan korrigere feilaktige opplysninger</li>
                <li><strong>Sletting:</strong> Du kan be om at vi sletter dine data ("retten til å bli glemt")</li>
                <li><strong>Dataportabilitet:</strong> Du kan få utlevert dine data i et maskinlesbart format</li>
                <li><strong>Innsigelse:</strong> Du kan protestere mot visse typer behandling</li>
                <li><strong>Begrensning:</strong> Du kan be om begrenset behandling</li>
                <li><strong>Tilbaketrekking av samtykke:</strong> Du kan når som helst trekke tilbake samtykke</li>
              </ul>
              <p className="text-slate-600 mt-3">
                For å utøve dine rettigheter, kontakt oss på personvern@minitask.no.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                10. Datalagring og sikkerhet
              </h2>
              <p className="text-slate-600">
                Dine data lagres sikkert på servere i EU/EØS-området. Vi bruker følgende 
                sikkerhetstiltak:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-3">
                <li>SSL/TLS-kryptering for all dataoverføring</li>
                <li>Passord hashes med bcrypt-algoritmen</li>
                <li>Regelmessige sikkerhetsvurderinger</li>
                <li>Begrenset tilgang til personopplysninger</li>
              </ul>
              <p className="text-slate-600 mt-3">
                Vi oppbevarer dine data så lenge kontoen din er aktiv, eller så lenge det er 
                nødvendig for å oppfylle formålene beskrevet i denne erklæringen.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                11. Lagringstid
              </h2>
              <p className="text-slate-600">
                Vi lagrer dine personopplysninger i følgende perioder:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-3">
                <li><strong>Kontodata:</strong> Så lenge kontoen er aktiv, pluss 30 dager etter sletting</li>
                <li><strong>Meldinger:</strong> 2 år etter siste aktivitet</li>
                <li><strong>Transaksjonsdata:</strong> 5 år (regnskapsloven)</li>
                <li><strong>Analytisk data:</strong> 26 måneder (anonymisert)</li>
                <li><strong>Cookies:</strong> Varierer, se cookie-oversikten</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                12. Sletting av konto
              </h2>
              <p className="text-slate-600">
                Du kan når som helst slette kontoen din via innstillingene eller ved å kontakte oss. 
                Ved sletting vil alle dine personopplysninger bli fjernet fra våre systemer, med 
                unntak av data vi er pålagt å oppbevare etter lov (f.eks. transaksjonshistorikk 
                for regnskapsformål i 5 år).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                13. Mindreåriges personvern
              </h2>
              <p className="text-slate-600">
                Minitask er åpen for brukere fra 14 år. For brukere under 16 år anbefaler vi 
                at foreldre/foresatte gjennomgår denne personvernerklæringen sammen med barnet. 
                Vi samler ikke bevisst inn mer informasjon enn nødvendig fra mindreårige.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                14. Kontakt oss
              </h2>
              <p className="text-slate-600">
                Hvis du har spørsmål om personvern, ønsker å utøve dine rettigheter, eller 
                vil klage på vår behandling av personopplysninger, kontakt oss på:
              </p>
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-slate-700">
                  <strong>E-post:</strong> personvern@minitask.no<br />
                  <strong>Behandlingsansvarlig:</strong> Minitask AS
                </p>
              </div>
              <p className="text-slate-600 mt-3">
                Du har også rett til å klage til Datatilsynet hvis du mener at vi ikke har 
                behandlet dine personopplysninger i samsvar med loven.
              </p>
              <a 
                href="https://www.datatilsynet.no" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#1E3A5F] hover:underline text-sm"
              >
                www.datatilsynet.no
              </a>
            </section>

            <section>
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-3">
                15. Endringer i personvernerklæringen
              </h2>
              <p className="text-slate-600">
                Vi kan oppdatere denne personvernerklæringen fra tid til annen. Ved vesentlige 
                endringer vil vi varsle deg via e-post eller en melding på plattformen minst 
                30 dager før endringene trer i kraft. Mindre endringer vil bli publisert på 
                denne siden med oppdatert dato.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
